import zipObject from 'lodash/zipObject';
import isEqual from 'lodash/isEqual';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import { fetchPermissions } from './fetchPermissions.tsx';
import { fetchPermissionsForProjects } from './fetchPermissionsForProjects.tsx';
import type { Permission } from './PermissionsResponse.types.tsx';
import type { State, Props } from './State.types.tsx';

const isPreviewContributorModeAllowed = (permissions: Permission[]) => {
	const canCreateIssues = permissions.find(
		({ key, havePermission }) => key === 'CREATE_ISSUES' && havePermission === true,
	);
	const canEditIssues = permissions.find(
		({ key, havePermission }) => key === 'EDIT_ISSUES' && havePermission === true,
	);
	return !!(canCreateIssues && canEditIssues);
};

export const loadPermissions =
	(): Action<State, Props> =>
	async (
		{ getState, setState },
		{ isSharedView, isCollectionView, isEmbeddedView, projectIds, onLoadStart, onLoadEnd },
	) => {
		onLoadStart?.();
		const loadingProps = {
			isSharedView,
			isCollectionView,
			isEmbeddedView,
			projectIds,
		};
		const {
			meta: { loadingProps: previousLoadingProps, loading, loaded },
			permissionsByProjectId: previousPermissions,
			isPreviewContributorMode: previousIsPreviewMode,
		} = getState();
		const isProjectView = !isSharedView && !isCollectionView;
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const currUrl = new URL(window.location.href);
		const isPreviewRolePresentInUrl = currUrl.searchParams.get('previewRole') === 'contributor'; // for 1st version only contributor role is available for preview mode
		const isPreviewMode =
			isPreviewRolePresentInUrl &&
			isProjectView &&
			isPreviewContributorModeAllowed(Object.values(previousPermissions)[0] ?? []);

		// Only load again when the props this function depends on have changed
		const isPropsNotChanged =
			Object.values(previousPermissions).length > 0 &&
			previousLoadingProps !== undefined &&
			previousIsPreviewMode === isPreviewMode &&
			isEqual(projectIds, previousLoadingProps?.projectIds);

		if (previousLoadingProps?.isCollectionView !== isCollectionView) {
			setState({
				meta: { loaded, loading, loadingProps },
			});
		}

		if (!isSharedView && isPropsNotChanged) {
			return;
		}

		if (!previousLoadingProps) {
			// init loading props
			setState({
				meta: { loaded: false, loading: false, error: undefined, loadingProps },
				permissionsByProjectId: {},
			});
		}

		if (!projectIds?.length) {
			return;
		}

		// do not re-fetch permissions
		if (
			(loading || loaded) &&
			projectIds !== undefined &&
			isEqual(projectIds, previousLoadingProps?.projectIds)
		) {
			return;
		}

		setState({
			meta: { loaded: false, loading: true, error: undefined, loadingProps },
			permissionsByProjectId: {},
		});

		try {
			let permissionsByProjectId: Record<string, Permission[]>;
			let previewContributorPermissions: Permission[];

			if (fg('jpd_multi-project-permission-fetch')) {
				permissionsByProjectId = await fetchPermissionsForProjects(projectIds.map(Number));
				previewContributorPermissions = permissionsByProjectId[projectIds[0]] ?? [];
			} else {
				const allPermissions = await Promise.all(projectIds.map(Number).map(fetchPermissions));
				permissionsByProjectId = zipObject(projectIds, allPermissions);
				previewContributorPermissions = allPermissions[0] ?? [];
			}

			setState({
				meta: {
					loaded: true,
					loading: false,
					error: undefined,
					loadingProps,
				},
				permissionsByProjectId,
				isPreviewContributorMode: isProjectView
					? isPreviewContributorModeAllowed(previewContributorPermissions) &&
						isPreviewRolePresentInUrl
					: false,
			});
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setState({
				meta: {
					loaded: true,
					loading: false,
					error,
					loadingProps,
				},
				permissionsByProjectId: {},
				isPreviewContributorMode: false,
			});
		} finally {
			onLoadEnd?.();
		}
	};
